import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { Plus, Trash2, CheckCircle, AlertTriangle, Clock, RefreshCw, Pencil, Smartphone, Banknote } from 'lucide-react';
import api from '../../api';

const fmt = (n) => `$${(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const getArgToday = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' });

function getDaysLabel(dueDate) {
  const today = getArgToday();
  const tomorrowDate = new Date(today + 'T12:00:00');
  tomorrowDate.setDate(tomorrowDate.getDate() + 1);
  const tomorrowStr = tomorrowDate.toLocaleDateString('en-CA');

  if (dueDate < today) return { label: 'VENCIDO', color: '#ff5252', urgent: true };
  if (dueDate === today) return { label: 'Hoy', color: '#FF9800', urgent: true };
  if (dueDate === tomorrowStr) return { label: 'Mañana', color: '#FFD93D', urgent: true };

  const diff = Math.ceil((new Date(dueDate + 'T12:00:00') - new Date(today + 'T12:00:00')) / (1000 * 60 * 60 * 24));
  return { label: `En ${diff} días`, color: 'var(--text2)', urgent: false };
}

function formatDate(dateStr) {
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
}

const EMPTY_FORM = { description: '', amount: '', due_date: '', expense_type: 'Extra', is_recurring: false, recurrence_type: 'Mes' };

const EXPENSE_TYPE_OPTS = [
  { type: 'Fijo', color: '#FF6B6B' },
  { type: 'Insumos', color: '#4ECDC4' },
  { type: 'Extra', color: '#FFD93D' },
];

export default function PendingExpenses() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);

  // Form modal (create / edit)
  const [showFormModal, setShowFormModal] = useState(false);
  const [editItem, setEditItem] = useState(null); // null = create, object = edit
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Pay modal
  const [showPayModal, setShowPayModal] = useState(false);
  const [payItem, setPayItem] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentAlert, setPaymentAlert] = useState(false);
  const [paying, setPaying] = useState(false);

  // Delete confirm modal
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    if (!user?.id) return;
    try {
      const r = await api.get(`/pending-expenses/${user.id}`);
      setItems(r.data);
    } catch (e) { console.error(e); }
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  // ── Create ─────────────────────────────────────────────
  const openCreate = () => {
    setEditItem(null);
    setForm(EMPTY_FORM);
    setShowFormModal(true);
  };

  // ── Edit ───────────────────────────────────────────────
  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      description: item.description,
      amount: String(item.amount),
      due_date: item.due_date,
      expense_type: item.expense_type || 'Extra',
      is_recurring: item.is_recurring,
      recurrence_type: item.recurrence_type || 'Mes',
    });
    setShowFormModal(true);
  };

  const saveForm = async (e) => {
    e.preventDefault();
    if (!form.description || !form.amount || !form.due_date) {
      alert('Completa todos los campos requeridos');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        description: form.description,
        amount: parseFloat(form.amount),
        due_date: form.due_date,
        expense_type: form.expense_type,
        is_recurring: form.is_recurring,
        recurrence_type: form.is_recurring ? form.recurrence_type : null,
      };
      if (editItem) {
        await api.patch(`/pending-expenses/${editItem.id}`, payload);
      } else {
        await api.post('/pending-expenses', { business_user_id: user.id, ...payload });
      }
      setShowFormModal(false);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // ── Pay ────────────────────────────────────────────────
  const openPay = (item) => {
    setPayItem(item);
    setPaymentMethod('');
    setPaymentAlert(false);
    setShowPayModal(true);
  };

  const confirmPay = async () => {
    if (!paymentMethod) {
      setPaymentAlert(true);
      setTimeout(() => setPaymentAlert(false), 3000);
      return;
    }
    setPaying(true);
    try {
      await api.put(`/pending-expenses/${payItem.id}/pay`, { payment_method: paymentMethod });
      setShowPayModal(false);
      setPayItem(null);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al registrar pago');
    } finally {
      setPaying(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────
  const openDelete = (item) => {
    setDeleteItem(item);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/pending-expenses/${deleteItem.id}`);
      setDeleteItem(null);
      load();
    } catch (err) {
      alert('Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };

  const urgentItems = items.filter(item => getDaysLabel(item.due_date).urgent);

  // Split into active (≤10 days) and upcoming (>10 days)
  const tenDaysLater = (() => {
    const today = getArgToday();
    const d = new Date(today + 'T12:00:00');
    d.setDate(d.getDate() + 10);
    return d.toLocaleDateString('en-CA');
  })();
  const activeItems = items.filter(item => item.due_date <= tenDaysLater);
  const upcomingItems = items.filter(item => item.due_date > tenDaysLater);

  // Only active items (≤10 days) count toward the summary total
  const activeTotal = activeItems.reduce((s, e) => s + e.amount, 0);

  const renderCard = (item, isUpcoming = false) => {
    const { label, color } = getDaysLabel(item.due_date);
    return (
      <div
        key={item.id}
        data-testid={`pending-expense-item-${item.id}`}
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 14,
          padding: 16,
          marginBottom: 12,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
              {(() => {
                const typeColors = { Fijo: '#FF6B6B', Insumos: '#4ECDC4', Extra: '#FFD93D' };
                const tc = typeColors[item.expense_type] || '#FFD93D';
                return (
                  <span style={{ fontSize: '12px', background: `${tc}20`, color: tc, padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>
                    {item.expense_type || 'Extra'}
                  </span>
                );
              })()}
              <span style={{ fontSize: '12px', background: 'rgba(74,158,255,0.12)', color: '#4A9EFF', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>
                A Pagar
              </span>
              {item.is_recurring && (
                <span style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: 3, background: 'rgba(93,230,200,0.1)', color: 'var(--accent)', padding: '2px 7px', borderRadius: 4, fontWeight: 600 }}>
                  <RefreshCw size={10} /> {item.recurrence_type}
                </span>
              )}
            </div>
            <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {item.description}
            </div>
            <div style={{ fontSize: '22px', fontWeight: 800, color: '#ff5252', marginBottom: 8 }}>
              {fmt(item.amount)}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <Clock size={13} color="var(--text2)" />
              <span style={{ fontSize: '13px', color: 'var(--text2)' }}>Vence: {formatDate(item.due_date)}</span>
              <span style={{ fontSize: '11px', fontWeight: 700, color, background: `${color}20`, padding: '1px 7px', borderRadius: 4 }}>{label}</span>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end', flexShrink: 0 }}>
            {/* Pagado and edit only visible for active items */}
            {!isUpcoming && (
              <button
                data-testid={`pay-pending-${item.id}`}
                onClick={() => openPay(item)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '9px 14px', borderRadius: 8,
                  background: 'linear-gradient(135deg, #4CAF50, #66BB6A)',
                  color: '#fff', border: 'none', cursor: 'pointer',
                  fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap',
                }}
              >
                <CheckCircle size={15} /> Pagado
              </button>
            )}
            <div style={{ display: 'flex', gap: 6 }}>
              {!isUpcoming && (
                <button
                  data-testid={`edit-pending-${item.id}`}
                  onClick={() => openEdit(item)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4A9EFF', padding: 4, display: 'flex', alignItems: 'center' }}
                >
                  <Pencil size={16} />
                </button>
              )}
              <button
                data-testid={`delete-pending-${item.id}`}
                onClick={() => openDelete(item)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', padding: 4, display: 'flex', alignItems: 'center' }}
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="page-full">
      <div className="header">
        <button className="icon-btn" data-testid="back-btn" onClick={() => navigate(-1)}>←</button>
        <h1 className="header-title">Gastos a Pagar</h1>
        <button className="add-btn" data-testid="add-pending-expense-btn" onClick={openCreate}>
          <Plus size={22} strokeWidth={2.5} />
        </button>
      </div>

      <div className="content-scroll">

        {/* Urgent alert */}
        {urgentItems.length > 0 && (
          <div
            data-testid="urgent-pending-alert"
            style={{
              background: 'rgba(255,82,82,0.1)',
              border: '1px solid #ff5252',
              borderRadius: 12,
              padding: '14px 16px',
              marginBottom: 16,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <AlertTriangle size={22} color="#ff5252" strokeWidth={1.5} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: '#ff5252', fontSize: '14px' }}>
                {urgentItems.length} gasto(s) próximo(s) a vencer
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text2)', marginTop: 2 }}>
                {urgentItems.map(i => i.description).join(' · ')}
              </div>
            </div>
          </div>
        )}

        {/* Summary — only active (Recordatorios) items */}
        {activeItems.length > 0 && (
          <div
            data-testid="pending-summary-card"
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderLeft: '4px solid #4A9EFF',
              borderRadius: 12,
              padding: '16px',
              marginBottom: 16,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div>
              <div style={{ fontSize: '12px', color: 'var(--text2)', marginBottom: 2 }}>Total Pendiente</div>
              <div style={{ fontSize: '26px', fontWeight: 800, color: '#4A9EFF' }}>{fmt(activeTotal)}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '12px', color: 'var(--text2)', marginBottom: 2 }}>Recordatorios</div>
              <div style={{ fontSize: '26px', fontWeight: 800 }}>{activeItems.length}</div>
            </div>
          </div>
        )}

        {/* Empty state */}
        {items.length === 0 && (
          <div className="empty-state">
            <span className="empty-icon"><Clock size={52} strokeWidth={1} /></span>
            <p className="empty-text">No hay gastos programados</p>
            <p className="empty-subtext">Usá el botón + para agregar recordatorios</p>
          </div>
        )}

        {/* ── Active Recordatorios (≤ 10 days) ────────────── */}
        {activeItems.length > 0 && <h2 className="section-title">Recordatorios</h2>}
        {activeItems.map(item => renderCard(item))}

        {/* ── Próximos pagos (> 10 days) — 50% opacity ─────── */}
        {upcomingItems.length > 0 && (
          <>
            <h2 className="section-title" style={{ marginTop: activeItems.length > 0 ? 16 : 0 }}>
              Próximos pagos
            </h2>
            <p style={{ fontSize: '12px', color: 'var(--text2)', marginBottom: 10, marginTop: -6 }}>
              Se actualizará a la lista de Recordatorio 10 días antes de su vencimiento para poder editar y pagar
            </p>
            <div style={{ opacity: 0.5 }}>
              {upcomingItems.map(item => renderCard(item, true))}
            </div>
          </>
        )}
      </div>

      {/* ── Create / Edit Modal ─────────────────────────── */}
      {showFormModal && (
        <div className="modal-overlay" onClick={() => setShowFormModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">{editItem ? 'Editar Recordatorio' : 'Nuevo Recordatorio'}</h3>
            <form onSubmit={saveForm}>
              <div className="form-group">
                <label className="label">Descripción *</label>
                <input
                  className="input"
                  data-testid="pending-description-input"
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="Ej: Alquiler local, Internet..."
                  required
                />
              </div>
              <div className="form-group">
                <label className="label">Tipo de gasto *</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {EXPENSE_TYPE_OPTS.map(opt => (
                    <button
                      key={opt.type}
                      type="button"
                      data-testid={`pending-type-${opt.type.toLowerCase()}`}
                      onClick={() => setForm({ ...form, expense_type: opt.type })}
                      style={{
                        flex: 1,
                        padding: '10px 4px',
                        borderRadius: 10,
                        border: `2px solid ${form.expense_type === opt.type ? opt.color : 'var(--border)'}`,
                        background: form.expense_type === opt.type ? `${opt.color}20` : 'var(--surface)',
                        color: form.expense_type === opt.type ? opt.color : 'var(--text2)',
                        fontWeight: form.expense_type === opt.type ? 700 : 500,
                        fontSize: '14px',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                      }}
                    >
                      {opt.type}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="label">Monto *</label>
                <input
                  className="input"
                  type="number"
                  step="0.01"
                  data-testid="pending-amount-input"
                  value={form.amount}
                  onChange={e => setForm({ ...form, amount: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>
              <div className="form-group">
                <label className="label">Fecha de vencimiento *</label>
                <input
                  className="input"
                  type="date"
                  data-testid="pending-due-date-input"
                  value={form.due_date}
                  onChange={e => setForm({ ...form, due_date: e.target.value })}
                  required
                />
              </div>

              <div
                className="form-group"
                style={{ background: 'rgba(74,158,255,0.07)', border: '1px solid rgba(74,158,255,0.2)', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <input
                    type="checkbox"
                    id="pending-recurring"
                    data-testid="pending-recurring-check"
                    checked={form.is_recurring}
                    onChange={e => setForm({ ...form, is_recurring: e.target.checked })}
                    style={{ width: 18, height: 18, marginTop: 2, accentColor: '#4A9EFF', cursor: 'pointer', flexShrink: 0 }}
                  />
                  <label htmlFor="pending-recurring" style={{ cursor: 'pointer', fontSize: '0.88rem', lineHeight: 1.4 }}>
                    <strong>Pago recurrente</strong><br />
                    <span style={{ color: 'var(--text2)', fontSize: '0.78rem' }}>
                      Al marcar como pagado se reprogramará automáticamente
                    </span>
                  </label>
                </div>
                {form.is_recurring && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    {['Semana', 'Mes', 'Año'].map(r => (
                      <button
                        key={r}
                        type="button"
                        data-testid={`recurrence-${r.toLowerCase()}`}
                        className={`freq-btn ${form.recurrence_type === r ? 'active' : ''}`}
                        style={form.recurrence_type === r ? { background: '#4A9EFF', borderColor: '#4A9EFF', color: '#fff' } : {}}
                        onClick={() => setForm({ ...form, recurrence_type: r })}
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="modal-buttons">
                <button type="button" className="btn-secondary" onClick={() => setShowFormModal(false)}>Cancelar</button>
                <button
                  type="submit"
                  data-testid="generate-pending-btn"
                  disabled={saving}
                  style={{
                    flex: 1, padding: 14, borderRadius: 12,
                    background: 'linear-gradient(135deg, #4A9EFF, #6AB8FF)',
                    color: '#fff', fontSize: 16, fontWeight: 700,
                    border: 'none', cursor: 'pointer', opacity: saving ? 0.6 : 1,
                  }}
                >
                  {saving ? 'Guardando...' : editItem ? 'Guardar Cambios' : 'Generar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Pay Modal (payment method selection) ────────── */}
      {showPayModal && payItem && (
        <div className="modal-overlay" onClick={() => setShowPayModal(false)}>
          <div className="modal-content modal-center" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Registrar Pago</h3>

            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: '15px', color: 'var(--text2)', marginBottom: 6 }}>{payItem.description}</div>
              <div style={{ fontSize: '32px', fontWeight: 800, color: '#4CAF50' }}>{fmt(payItem.amount)}</div>
              {payItem.is_recurring && (
                <div style={{ fontSize: '12px', color: 'var(--accent)', marginTop: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                  <RefreshCw size={12} /> Se reprogramará para el próximo {payItem.recurrence_type?.toLowerCase()}
                </div>
              )}
            </div>

            <p className="label" style={{ marginBottom: 10 }}>Forma de pago *</p>

            {paymentAlert && (
              <div className="payment-alert">
                <AlertTriangle size={16} style={{ marginRight: 6 }} /> Seleccioná una forma de pago
              </div>
            )}

            <div className="payment-row" style={{ marginBottom: 20 }}>
              <button
                data-testid="pay-method-efectivo"
                className={`payment-btn ${paymentMethod === 'efectivo' ? 'active' : ''}`}
                onClick={() => { setPaymentMethod('efectivo'); setPaymentAlert(false); }}
              >
                <Banknote size={18} style={{ marginRight: 6 }} /> Efectivo
              </button>
              <button
                data-testid="pay-method-transferencia"
                className={`payment-btn ${paymentMethod === 'transferencia' ? 'active' : ''}`}
                onClick={() => { setPaymentMethod('transferencia'); setPaymentAlert(false); }}
              >
                <Smartphone size={18} style={{ marginRight: 6 }} /> Transferencia
              </button>
            </div>

            <div className="modal-buttons">
              <button className="btn-secondary" onClick={() => setShowPayModal(false)}>Cancelar</button>
              <button
                data-testid="confirm-pay-btn"
                onClick={confirmPay}
                disabled={paying}
                style={{
                  flex: 1, padding: 14, borderRadius: 12,
                  background: 'linear-gradient(135deg, #4CAF50, #66BB6A)',
                  color: '#fff', fontSize: 16, fontWeight: 700,
                  border: 'none', cursor: 'pointer', opacity: paying ? 0.6 : 1,
                }}
              >
                {paying ? 'Registrando...' : 'Confirmar Pago'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm Modal ─────────────────────────── */}
      {deleteItem && (
        <div className="modal-overlay" onClick={() => setDeleteItem(null)}>
          <div className="modal-content modal-center" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Eliminar Recordatorio</h3>
            <p style={{ textAlign: 'center', color: 'var(--text2)', marginBottom: 20 }}>
              ¿Eliminar <strong style={{ color: 'var(--text)' }}>{deleteItem.description}</strong>?
              <br />Esta acción no se puede deshacer.
            </p>
            <div className="modal-buttons">
              <button className="btn-secondary" onClick={() => setDeleteItem(null)}>Cancelar</button>
              <button
                data-testid="confirm-delete-btn"
                className="btn-danger"
                onClick={confirmDelete}
                disabled={deleting}
                style={{ flex: 1 }}
              >
                {deleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
